import {
  CellTemplate,
  Column,
  Compatible,
  Id,
  Row,
  Uncertain,
  getCellProperty,
} from "@silevis/reactgrid";
import { ResponseField, CustomCell, AnchorCell } from "./ScheduledFeed-models";
import ScheduledFeedServiceProvider from "./scheduleFeed-services";
import {
  IUpdateScheduleSubStatusModel,
} from "../../../generated/generated-proxies";
import { toast } from "react-toastify";
import AnchorTag from "./Schedule-anchor-tag";
import _ from "lodash";

export class ScheduledFeedPresenter {
  private serviceProvider = new ScheduledFeedServiceProvider();
  currentPage = 1;
  pageSize = 10000;

  tableHeaderRow: Row = {
    rowId: "header",
    height: 150,
    cells: [
      { type: "header", text: "" },
      { type: "header", text: "Edit Cell" },
      { type: "header", text: "Week No" },
      { type: "header", text: "Production Start Date" },
      { type: "header", text: "View Order" },
      { type: "header", text: "Chassis No" },
      { type: "header", text: "Cusomter Name" },
      { type: "header", text: "Model" },
      { type: "header", text: "Dealer" },
      { type: "header", text: "Status" },
      { type: "header", text: "Completion Date" },
      { type: "header", text: "Lock" },
      { type: "header", text: "Notes" },
      { type: "header", text: "Weekly Target" },
      { type: "header", text: "Orders Count" },
      { type: "header", text: "Empty Spot" },
      { type: "header", text: "Chassis Notes" },
      { type: "header", text: "Electrical System" },
      { type: "header", text: "Upgrade Pack" },
      { type: "header", text: "Prod Value" },
      { type: "header", text: "Drawn By" },
      { type: "header", text: "Drawn Date" },
      { type: "header", text: "COT's Completion Date" },
    ],
  };

  tableColumns: Column[] = [
    { columnId: "Drag", width: 30 },
    { columnId: "Edit", width: 20 },
    { columnId: "week no", width: 40 },
    { columnId: "productionStartDate", width: 240 },
    { columnId: "link", width: 50 },
    { columnId: "chassisNo", width: 80 },
    { columnId: "customer name", width: 140 },
    { columnId: "model", width: 290 },
    { columnId: "dealer", width: 230 },
    { columnId: "status", width: 150 },
    { columnId: "completiondate", width: 240 },
    { columnId: "lock", width: 30 },
    { columnId: "notes", width: 60 },
    { columnId: "weeklyTarget", width: 40 },
    { columnId: "ordersCount", width: 40 },
    { columnId: "emptySpot", width: 40 },
    { columnId: "cahssisNotes", width: 100 },
    { columnId: "electricalSystem", width: 100 },
    { columnId: "upgradePack", width: 60 },
    { columnId: "productionValue", width: 30 },
    { columnId: "drawnBy", width: 60 },
    { columnId: "drawnDate", width: 110 },
    { columnId: "completion date", width: 115 },
  ];

  AnchorCellTemplate: CellTemplate<AnchorCell> = {
    getCompatibleCell: (
      uncertainCell: Uncertain<AnchorCell>
    ): Compatible<AnchorCell> => {
      const text = getCellProperty(uncertainCell, "text", "string");
      const link = getCellProperty(uncertainCell, "link", "string");
      const value = getCellProperty(uncertainCell, "value", "number");
      return { ...uncertainCell, text, link, value, type: "anchor" };
    },
    handleKeyDown: (
      cell: Compatible<AnchorCell>,
      keyCode: number,
      ctrl: boolean,
      shift: boolean,
      alt: boolean,
      key?: string | undefined
    ) => {
      return { cell, enableEditMode: false };
    },
    update: (
      cell: Compatible<AnchorCell>,
      cellToMerge: Partial<AnchorCell>
    ): Compatible<AnchorCell> => ({
      ...cell,
      ...cellToMerge,
    }),
    getClassName: (): string => "",
    render: (
      cell: Compatible<AnchorCell>,
      isInEditMode: boolean,
      onCellChanged: (cell: Compatible<AnchorCell>, commit: boolean) => void
    ): React.ReactNode => AnchorTag(cell.link, cell.text),
  };

  formatDate = (dateString: any, isShortFormat: boolean = false) => {
    if (!dateString) {
      return "";
    }

    const date = new Date(dateString);

    if (isShortFormat) {
      // Short format MM/DD/YYYY
      const formattedDate = new Intl.DateTimeFormat('en-US', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(date);
      return formattedDate;
    } else {
      // Long format with weekday
      const formattedDate = new Intl.DateTimeFormat('en-US', {
        weekday: 'long', // Include the day of the week
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      }).format(date);
      return formattedDate;
    }
  };



  getRows = (field: ResponseField[]): Row[] => [
    this.tableHeaderRow,
    ...field.map<Row>((res, idx) => ({
      rowId: idx,
      reorderable: true,
      cells: [
        {
          type: "text",
          text: "...",
          style: {
            backgroundColor: this.setBgColorToRows(res, idx),
          },
        },
        {
          type: "text",
          text: "",
          style: {
            backgroundColor: this.setBgColorToRows(res, idx),
          },
        },
        {
          type: "text",
          text: res.weekNo?.toString() ?? "", // Convert to string
          style: {
            backgroundColor: this.setBgColorToRows(res, idx),
          },
        },

        {
          type: "text",
          text: this.formatDate(res.productionStartDate, false) ?? "",
          style: {
            backgroundColor: this.setBgColorToRows(res, idx),
          },
        },
        // {
        //   type: "email",
        //   text: res.OrderLink ?? "",
        //   style: {
        //     backgroundColor: res.isReserved
        //       ? "#a3d7a3"
        //       : idx % 2 === 0
        //       ? "#fff"
        //       : "#eaeae8",
        //   },
        // },
        {
          type: "anchor",
          text: `${res.orderLink ? "Link" : ""}`, // Display text
          link: res.orderLink ?? "", // Actual URL
          value: 0, // Dummy value
          style: {
            backgroundColor: this.setBgColorToRows(res, idx),
          },
        } as AnchorCell,
        {
          type: "text",
          text: res.serial ?? "",
          style: {
            backgroundColor: this.setBgColorToRows(res, idx),
          },
        },
        {
          type: "text",
          text: res.jobContactLastName ?? "",
          style: {
            backgroundColor: this.setBgColorToRows(res, idx),
          },
        },
        {
          type: "text",
          text: res.model ?? "",
          style: {
            backgroundColor: this.setBgColorToRows(res, idx),
          },
        },
        {
          type: "text",
          text: res.dealer ?? "",
          style: {
            backgroundColor: this.setBgColorToRows(res, idx),
          },
        },
        {
          type: "text",
          text: res.status ?? "",
          style: { background: this.checkIsAllOptionSelected(res, idx) },
        },
        {
          type: "text",
          text: this.formatDate(res.completionDate, false) ?? "",
          style: {
            backgroundColor: this.setBgColorToRows(res, idx),
          },
        },
        {
          type: "text",
          text: "",
          style: {
            backgroundColor: this.setBgColorToRows(res, idx),
          },
          className: `${res.isLocked ? "tick" : "cross"}`,
          rawData: res.isLocked,
        },
        {
          type: "text",
          text: res.notes ?? "",
          style: {
            backgroundColor: this.setBgColorToRows(res, idx),
          },
        },
        {
          type: "text",
          text: res.weeklyTarget?.toString() ?? "",
          style: {
            backgroundColor: this.setBgColorToRows(res, idx),
          },
        },
        {
          type: "text",
          text: res.ordersCount?.toString() ?? "",
          style: {
            backgroundColor: this.setBgColorToRows(res, idx),
          },
        },
        {
          type: "text",
          text: res.emptySpot?.toString() ?? "",
          style: {
            backgroundColor: this.setBgColorToRows(res, idx),
          },
        },
        {
          type: "text",
          text:
            res.chassisNotes ??
            this.chesisNotesFieldAutoInput(res.description ?? ""),
          style: {
            backgroundColor: this.setBgColorToRows(res, idx),
          },
        },
        {
          type: "text",
          text:
            res.electricalSystem ??
            this.electricalSystemFieldAutoInput(res.description ?? ""),
          style: {
            backgroundColor: this.setBgColorToRows(res, idx),
          },
        },
        {
          type: "text",
          text:
            res.upgradePack ??
            this.upgradePackFieldAutoInput(res.description ?? ""),
          style: {
            backgroundColor: this.setBgColorToRows(res, idx),
          },
        },
        {
          type: "text",
          text: res.productionValue ?? "",
          style: {
            backgroundColor: this.setBgColorToRows(res, idx),
          },
        },
        {
          type: "text",
          text: res.drawnBy ?? "",
          style: {
            backgroundColor: this.setBgColorToRows(res, idx),
          },
        },
        {
          type: "text",
          text: this.formatDate(res.drawnDate, true) ?? "",
          style: {
            backgroundColor: this.setBgColorToRows(res, idx),
          },
        },
        {
          type: "text",
          text: this.formatDate(res.preferredCompletion, true) ?? "",
          style: {
            backgroundColor: this.setBgColorToRows(res, idx),
          },
        },
      ] as CustomCell[],
    })),
  ];

  setBgColorToRows(row: ResponseField, id: number) {
    return row.isFound
      ? "#f1f15d"
      : row.isReserved
        ? "#a3d7a3"
        : id % 2 === 0
          ? "#fff"
          : "#eaeae8";
  }

  checkIsAllOptionSelected = (row: any, idx: number) => {
    let backgroundColor = this.setBgColorToRows(row, idx);
    if (
      row.Status &&
      row.Status === "Scheduled" &&
      row.ScheduleSubStatus?.length < 9
    ) {
      backgroundColor = "#FFADB0";
    }
    return backgroundColor;
  };

  // getLineOptions() {
  //   return [
  //     { value: "main-line", displayText: "Main Line" },
  //     { value: "crv-line", displayText: "Crv Line" },
  //   ];
  // }

  async handleRearrange(selectedOption: string) {
    const response = await this.serviceProvider.rearrangeData(selectedOption);
    return response;
  }

  getMovingRowChassisList = (movingRowIds: Id[], data: ResponseField[]) => {
    const movingChassisNoList = movingRowIds.map((rowId: Id) => {
      // const chassisNo = data.find(item => item.id === rowId)
      const chassisNo = data[Number(rowId)]
      return chassisNo ? chassisNo.serial : null;
    })
    return movingChassisNoList;
  }
  getTargetRowCompletionDate = (targetRowId: Id, data: ResponseField[]) => {
    const targetRow = data.find(item => item.id === targetRowId);
    return targetRow?.completionDate ?? null;
  }

  reorderArray = (arr: ResponseField[], idxs: number[], to: number) => {
    const movedElements = arr.filter((_, idx) => idxs.includes(idx));
    const targetIdx =
      Math.min(...idxs) < to
        ? to + 1
        : to - idxs.filter((idx) => idx < to).length;
    const leftSide = arr.filter(
      (_, leftArrIdx) => leftArrIdx < targetIdx && !idxs.includes(leftArrIdx)
    );
    const rightSide = arr.filter(
      (_, rightArrayIdx) =>
        rightArrayIdx >= targetIdx && !idxs.includes(rightArrayIdx)
    );
    return [...leftSide, ...movedElements, ...rightSide];
  };

  getReorderedRows(target: number, movingRowIds: Id[], data: ResponseField[]) {
    let replacedIds = [];
    let oldRows = [];
    let newRows = [];
    const minVal = Math.min(...movingRowIds.map((id) => Number(id)));
    if (target > minVal) {
      //Up to Down
      replacedIds = this.range(target - movingRowIds.length + 1, target);
    } else {
      //Down to Up
      replacedIds = this.range(target, target + (movingRowIds.length - 1));
    }
    oldRows = movingRowIds.map((id) => data[Number(id)]);
    newRows = replacedIds.map((id) => data[Number(id)]);
    return { oldRows, newRows };
  }


  reorderDataRowAndSave(
    prevData: ResponseField[],
    targetRowId: Id,
    rowIds: Id[]
  ) {
    // const to = prevData.findIndex((item) => item.id === targetRowId);
    const to = Number(targetRowId);
    // const rowIndices = rowIds.map((id) =>
    //   prevData.findIndex((item) => item.id === id)
    // );
    const rowIndices = rowIds.map((id) =>
      Number(id)
    );
    const newData = this.reorderArray(prevData, rowIndices, to);
    // const updated_data = await this.serviceProvider.updateReorderScheduleData(
    //   selectedOption,
    //   newData
    // );
    let updatedData = _.map(newData, (data, ind) => {
      return { id: data.id, order: ind + 1 }
    })
    return updatedData;
  }

  async sendReorderedDataToDb(
    fromItem: ResponseField[],
    toItem: ResponseField[]
  ) {
    const response = await this.serviceProvider.sendReorderDatatoDb({
      fromItem,
      toItem,
    });
    return response;
  }

  electricalSystemFieldAutoInput = (field: string) => {
    field = field.toLowerCase();
    if (
      field.includes("enerdrive") &&
      field.includes(" i ") &&
      field.includes("400")
    ) {
      return "EN I 400";
    } else if (field.includes("victron")) {
      return "Victron";
    } else if (field.includes("redarc redvision") && field.includes("ilo")) {
      return "Redvision";
    } else if (field.includes("enerdrive 600") && field.includes(" i ")) {
      return "EN I 600";
    } else {
      return "";
    }
  };

  upgradePackFieldAutoInput = (field: string) => {
    field = field.toLowerCase();
    if (field.includes("excalibur") && field.includes("upgrade pack")) {
      return "EXUP";
    } else if (field.includes("all terrain upgrade")) {
      return "AT";
    } else if (field.includes("x country upgrade")) {
      return "XC";
    } else {
      return "";
    }
  };

  chesisNotesFieldAutoInput = (field: string) => {
    field = field.toLowerCase();
    if (field.includes("extreme off road") && field.includes("upgrade pack")) {
      return "Extreme off road";
    } else if (field.includes("xcountry")) {
      return "x countrky";
    } else if (field.includes("hot dipped galvanised")) {
      return "Hot dip Gal";
    } else if (field.includes("upgrade to stage 2 tandem axle airbags")) {
      return "Stage 2 Airbags";
    } else {
      return "";
    }
  };

  fillUpPositionField(data: ResponseField[]) {
    data.forEach(item => {
      item.position = 25 * item.order
    });
  }

  async loadLineData(
    selectedValue: string,
    query: string,
    refreshData: boolean
  ) {
    const queryStr = refreshData ? "" : query;
    if (refreshData) await this.triggerDataUpdate();
    const response = await this.fetchSelectedLineData(selectedValue, queryStr);
    this.fillUpPositionField(response);
    return response;
  }

  async fetchSelectedLineData(line_view: string, searchText: string) {
    const response = await this.serviceProvider.fetchLineData(
      line_view,
      searchText,
      this.currentPage,
      this.pageSize
    );
    return response;
  }

  triggerDataUpdate() {
    return this.serviceProvider.triggerDataUpdate();
  }

  async updateModalSwapOptions(
    textValues: any,
    rowId: number,
    selectedLine: string,
    fromChassisNo: string
  ) {
    const response = await this.serviceProvider.modalSwapOptionsUpdate(
      textValues,
      rowId,
      selectedLine,
      fromChassisNo
    );
    const data = response.data;
    if (!response.success) toast.error("Chassis No. not found");
    return data;
  }

  idFormatter = (ids: string[]) => ids.join(",");

  range(start: number, end: number) {
    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  }

  async handleModalDataChange(
    rowData: ResponseField,
    checkBoxIds: string[],
    selectedOption: string,
    reservationVal: string,
    lockState: boolean
  ) {
    const formattedSelectedIdsString = this.idFormatter(checkBoxIds);

    //Request Body
    const checkBoxIdRequestBody: IUpdateScheduleSubStatusModel = {
      jobId: rowData.jobId,
      scheduleSubStatus: formattedSelectedIdsString ?? "",
    };

    // const modalResponse = await this.serviceProvider.saveScheduleRowData(
    //   selectedOption,
    //   tableRequestBody
    // );
    await this.serviceProvider.updateModalCheckBoxData(checkBoxIdRequestBody);
    // return modalResponse;
  }

  async handleRemoveReserveItem(lineName: string, rowId: number) {
    const response = await this.serviceProvider.removeReservedRow(
      lineName,
      rowId
    );
    return response;
  }

  scrollToFilteredRow(data: ResponseField[]) {
    const filteredData = data.filter(item => item.isFound)
    this.moveScrollTo(filteredData[0].position);
  }

  moveScrollTo(position?: number) {
    if (position === undefined || position < 0) {
      return;
    }
    window.scrollTo(0, position);
  }
}

let debounceTimer: NodeJS.Timeout;
export const debounce = (func: any, timeout = 100) => {
  return (...args: any) => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      func(...args);
    }, timeout);
  };
};

const edgeSize = 20;
const edgeTop = 320;
let timer: NodeJS.Timeout;

export const handleDragRow = (event: any) => {
  // Get the viewport-relative coordinates of the mousemove event.
  const mousePointerY = event.clientY;

  // Get the viewport dimensions.
  const viewportHeight = document.documentElement.clientHeight;

  const edgeBottom = viewportHeight - edgeSize;
  const isInTopEdge = mousePointerY < edgeTop;
  const isInBottomEdge = mousePointerY > edgeBottom;

  if (!(isInTopEdge || isInBottomEdge)) {
    clearTimeout(timer);
    return;
  }

  const documentHeight = Math.max(
    document.body.scrollHeight,
    document.body.offsetHeight,
    document.body.clientHeight,
    document.documentElement.scrollHeight,
    document.documentElement.offsetHeight,
    document.documentElement.clientHeight
  );

  const maxScrollY = documentHeight - viewportHeight;

  (function checkForWindowScroll() {
    clearTimeout(timer);
    if (adjustWindowScroll()) {
      timer = setTimeout(checkForWindowScroll, 30);
    }
  })();

  // Adjust the window scroll based on the user's mouse position.
  function adjustWindowScroll() {
    // Get the current scroll position of the document.
    const currentScrollX = window.pageXOffset;
    const currentScrollY = window.pageYOffset;

    // Determine if the window can be scrolled in any particular direction.
    const canScrollUp = currentScrollY > 0;
    const canScrollDown = currentScrollY < maxScrollY;

    let nextScrollX = currentScrollX;
    let nextScrollY = currentScrollY;

    const maxStep = 50;

    // Should we scroll up?
    if (isInTopEdge && canScrollUp) {
      const intensity = (edgeTop - mousePointerY) / edgeSize;
      nextScrollY = nextScrollY - maxStep * intensity;

      // Should we scroll down?
    } else if (isInBottomEdge && canScrollDown) {
      const intensity = (mousePointerY - edgeBottom) / edgeSize;
      nextScrollY = nextScrollY + maxStep * intensity;
    }

    nextScrollY = Math.max(0, Math.min(maxScrollY, nextScrollY));
    if (nextScrollY !== currentScrollY) {
      window.scrollTo(nextScrollX, nextScrollY);
      return true;
    } else {
      return false;
    }
  }
};
